import { version, runningFromSource } from '#config/definitions';

const RELOAD_FLAG_KEY = '__app_version_reload_attempted';

// The main entry chunk is emitted as main.<contenthash>.js, so the hash of
// the running script tag identifies the exact build this page was loaded
// from
function getRunningBuildHash() {
  const mainScript = document.querySelector('script[src*="main."]');
  return mainScript?.src.match(/main\.([a-f0-9]+)\.js/)?.[1] || null;
}

/**
 * Compare this build's content hash against version.json, which the webpack
 * build emits into dist/ and which is deployed to S3 alongside the bundle.
 * The hash changes on every code change, so a mismatch means this page is
 * running a stale build (e.g. the document was served from a browser cache
 * that survived a deploy), and a fresh document load is required.
 */
export async function checkAppVersion() {
  // Local dev builds are not deployed, so there is no version.json to
  // compare against
  if (runningFromSource) {
    return;
  }

  const runningBuildHash = getRunningBuildHash();
  if (!runningBuildHash) {
    return;
  }

  // Allow at most one version-reload per browser session. If the mismatch
  // persists after the reload (e.g. S3 mid-deploy state), do not loop.
  if (sessionStorage.getItem(RELOAD_FLAG_KEY)) {
    sessionStorage.removeItem(RELOAD_FLAG_KEY);
    return;
  }

  let deployed;
  try {
    const response = await fetch(`version.json?r=${Date.now()}`, {
      cache: 'no-store',
    });
    if (!response.ok) {
      return;
    }
    deployed = await response.json();
  } catch (error) {
    // Offline or file not found: keep running the current build
    return;
  }

  if (!deployed?.buildHash || deployed.buildHash === runningBuildHash) {
    return;
  }

  console.warn(
    `Build mismatch: running ${runningBuildHash}, deployed ${deployed.buildHash}. ` +
      `Versions ${version} vs ${deployed.version}. Reloading for current build...`,
  );
  sessionStorage.setItem(RELOAD_FLAG_KEY, 'true');
  // A cache-busted URL guarantees a fresh document request
  window.location.replace(`${window.location.pathname}?r=${Date.now()}`);
}
